import {
  ActionBar,
  ActionInfo,
  Badge,
  Card,
  DataTable,
  EmptyState,
  Facts,
  FileThumb,
  Loading,
  Modal,
  Note,
  PageHead,
  Tabs,
  formatDate,
  formatMoney,
} from "@corecrm/ui";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fileUrl } from "../api/client";
import { useBudget, useDeliverables, useProject, useProjectFiles, useQuotes, useTasks } from "../api/queries";
import type { Budget, Deliverable, Project, ProjectFile, Task } from "../api/types";
import { Crumb, HoursBar, LoadError, NotAvailable, Status } from "../components/bits";
import { DeliverableActions } from "../components/DeliverableActions";
import { DeliverableFiles } from "../components/DeliverableFiles";
import { QuestionDialogById, QuestionsCard } from "../components/Questions";
import { ProjectComments, TaskComments, useUnreadComments } from "../components/ProjectComments";
import { BoardView, PlanView } from "../components/Sprints";
import { TaskList } from "../components/Tasks";
import { BugsView } from "../components/Bugs";
import { TestingView } from "../components/Testing";
import { FeatureRequestsView } from "../components/FeatureRequests";
import { BILLING_ONLY_NOTE, BILLING_MODEL, TERM, fileSize } from "../lib/labels";

/* A project: what's awaiting approval, phases, deliverables, shared tasks and the payment
   schedule (or the agreement, for maintenance and hosting). The prototype's pProject(). */
export function ProjectPage() {
  const { id = "", bugId, requestId, caseId } = useParams();
  const project = useProject(id);
  if (project.isPending) return <Loading />;
  if (project.error)
    return (
      <LoadError
        error={project.error}
        onRetry={() => project.refetch()}
        // A deep link says the same whether the project or the record is the one you can't see.
        what={bugId ? "bug" : requestId ? "feature request" : caseId ? "test" : "project"}
        back={["/projects", "Back to projects"]}
      />
    );
  // A deep link into a tab the contact can't use is as unavailable as a missing record.
  if (
    (bugId && !project.data.can_view_bugs) ||
    (requestId && !project.data.can_view_feature_requests) ||
    (caseId && !project.data.can_view_tests)
  )
    return (
      <NotAvailable
        what={bugId ? "bug" : requestId ? "feature request" : "test"}
        back={["/projects", "Back to projects"]}
      />
    );
  return <ProjectView project={project.data} />;
}

type View = "overview" | "plan" | "board" | "comments" | "bugs" | "feature-requests" | "testing";

/** "Planned Jan 5 – Mar 2", "Planned to end Mar 2", or null when there are no planned dates. */
function plannedDates(start: string | null, end: string | null): string | null {
  if (start && end) return `Planned ${formatDate(start)} – ${formatDate(end)}`;
  if (end) return `Planned to end ${formatDate(end)}`;
  if (start) return `Planned to start ${formatDate(start)}`;
  return null;
}

function ProjectView({ project: p }: { project: Project }) {
  const [params, setParams] = useSearchParams();
  const { bugId, requestId, caseId } = useParams();
  // A deep link to one record (/projects/:id/bugs/:bugId) opens that tab with the record open.
  const asked = bugId ? "bugs" : requestId ? "feature-requests" : caseId ? "testing" : params.get("view");
  // A question opened from its email: /projects/:id?question=…
  const question = params.get("question");
  const closeQuestion = () =>
    setParams(
      (now) => {
        now.delete("question");
        return now;
      },
      { replace: true },
    );
  // The Plan and Board exist only while the team shares its sprints with the client.
  const view: View =
    asked === "comments"
      ? "comments"
      : asked === "bugs" && p.can_view_bugs
        ? "bugs"
        : asked === "feature-requests" && p.can_view_feature_requests
          ? "feature-requests"
          : asked === "testing" && p.can_view_tests
            ? "testing"
            : p.client_sees_sprints && (asked === "plan" || asked === "board")
              ? asked
              : "overview";
  const unread = useUnreadComments(p.id);
  const [discussing, setDiscussing] = useState<Task | null>(null);
  const deliverables = useDeliverables(p.id);
  const tasks = useTasks(p.id);
  const budget = useBudget(p.id);
  const quotes = useQuotes();
  const waiting = (deliverables.data ?? []).filter((d) => d.can_respond);
  const first = waiting[0];
  const changes = (quotes.data ?? []).filter((q) => q.kind === "change_request" && q.project_id === p.id);
  const pendingChanges = changes.filter((q) => q.can_respond);
  const pastChanges = changes.filter((q) => !q.can_respond);
  const firstChange = pendingChanges[0];

  return (
    <>
      <Crumb to="/projects" label="Projects">
        {p.name}
      </Crumb>
      <PageHead
        title={p.name}
        sub={
          <>
            <Status status={p.status} />
            <Badge kind="info">{BILLING_MODEL[p.billing_model] ?? p.billing_model}</Badge>
            {p.started_at && <span>Started {formatDate(p.started_at)}</span>}
            {p.planned_end && <span>Planned end {formatDate(p.planned_end)}</span>}
            {/* UX-194: closed, and inside its warranty until this date. */}
            {p.status === "warranty" && p.warranty_ends_on && (
              <span>Warranty until {formatDate(p.warranty_ends_on)}</span>
            )}
          </>
        }
      />
      {p.status === "warranty" && p.warranty_ends_on && (
        <Note kind="acc">
          This project is finished and under warranty until <strong>{formatDate(p.warranty_ends_on)}</strong>.
          If something we delivered isn&apos;t working as it should, tell us in the comments and we&apos;ll
          fix it at no charge. Changes and new features are quoted separately.
        </Note>
      )}

      <Tabs
        active={view}
        items={[
          { key: "overview", label: "Overview", to: `/projects/${p.id}` },
          ...(p.client_sees_sprints
            ? [
                { key: "plan", label: "Plan", to: `/projects/${p.id}?view=plan` },
                { key: "board", label: "Board", to: `/projects/${p.id}?view=board` },
              ]
            : []),
          {
            key: "comments",
            label: "Comments",
            to: `/projects/${p.id}?view=comments`,
            count: unread.data ? unread.data : undefined,
          },
          ...(p.can_view_bugs ? [{ key: "bugs", label: "Bugs", to: `/projects/${p.id}?view=bugs` }] : []),
          ...(p.can_view_feature_requests
            ? [
                {
                  key: "feature-requests",
                  label: "Feature requests",
                  to: `/projects/${p.id}?view=feature-requests`,
                },
              ]
            : []),
          ...(p.can_view_tests
            ? [{ key: "testing", label: "Testing", to: `/projects/${p.id}?view=testing` }]
            : []),
        ]}
      />
      {view === "comments" && <ProjectComments projectId={p.id} highlight={params.get("thread")} />}
      {discussing && <TaskComments projectId={p.id} task={discussing} onClose={() => setDiscussing(null)} />}
      {question && <QuestionDialogById id={question} onClose={closeQuestion} />}

      {view === "bugs" && <BugsView project={p} />}
      {view === "feature-requests" && <FeatureRequestsView project={p} />}
      {view === "testing" && <TestingView project={p} />}
      {view === "plan" && <PlanView project={p} />}
      {view === "board" && <BoardView project={p} />}
      {view === "overview" && (
        <>
          {waiting.length > 0 && (
            <Card
              title="Awaiting your approval"
              action={<Badge kind="acc">{`${waiting.length} to review`}</Badge>}
            >
              <div className="item-list">
                {waiting.map((d) => (
                  <div className="item" key={d.id}>
                    <div className="what">
                      <Link to={`/deliverables/${d.id}`}>
                        <strong>{d.title}</strong>
                      </Link>
                      <span>
                        {d.phase_name} · submitted {formatDate(d.submitted_at)}
                        {d.files.length ? ` · ${d.files.length} file${d.files.length === 1 ? "" : "s"}` : ""}
                      </span>
                    </div>
                    <div className="row">
                      <DeliverableActions deliverable={d} size="sm" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {pendingChanges.length > 0 && (
            <Card
              title="Change requests awaiting your approval"
              id="p-cq-pending"
              action={<Badge kind="acc">{`${pendingChanges.length} to review`}</Badge>}
            >
              <div className="item-list">
                {pendingChanges.map((q) => (
                  <div className="item" key={q.id}>
                    <div className="what">
                      <strong>
                        {q.number}: {q.title}
                      </strong>
                      <span className="mono">
                        {q.total ? `${formatMoney(q.total)} incl. tax · ` : ""}valid until{" "}
                        {formatDate(q.valid_until)}
                      </span>
                    </div>
                    <Link className="btn sm primary" to={`/quotes/${q.id}`}>
                      Review change
                    </Link>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {p.agreement && <AgreementCard project={p} />}

          {p.phases.length > 0 && (
            <Card title="Phases">
              {p.phases.map((ph) => {
                const hours = budget.data?.phases.find((x) => x.phase_id === ph.id);
                return (
                  <div className="phase" key={ph.id}>
                    <div className="phase-h">
                      <strong>{ph.name}</strong>
                      <Status status={ph.status} />
                    </div>
                    {hours && (
                      <HoursBar
                        used={Number(hours.hours_used)}
                        budget={hours.budget_hours ? Number(hours.budget_hours) : null}
                        label={`${ph.name} hours used`}
                      />
                    )}
                    <div className="phase-m">
                      {ph.budget_amount && <span>Budget {formatMoney(ph.budget_amount)}</span>}
                      {plannedDates(ph.planned_start, ph.planned_end) && (
                        <span>{plannedDates(ph.planned_start, ph.planned_end)}</span>
                      )}
                      {ph.description && <span>{ph.description}</span>}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}

          <div className="grid2">
            <Card title="Deliverables">
              <DeliverableList deliverables={deliverables.data} pending={deliverables.isPending} />
            </Card>
            <BudgetSummary project={p} budget={budget.data} />
          </div>

          {pastChanges.length > 0 && (
            <Card title="Change history" id="p-cq-hist">
              <div className="item-list">
                {pastChanges.map((q) => (
                  <div className="item" key={q.id}>
                    <div className="what">
                      <strong>
                        {q.number}: {q.title}
                      </strong>
                      <span>
                        <Status status={q.status} /> · sent {formatDate(q.sent_at)}
                        {q.total && (
                          <>
                            {" · "}
                            <span className="mono">{formatMoney(q.total)}</span>
                          </>
                        )}
                      </span>
                    </div>
                    <Link className="btn sm" to={`/quotes/${q.id}`}>
                      View
                    </Link>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="Open Tasks">
            {tasks.isPending ? <Loading /> : <TaskList tasks={tasks.data ?? []} onComments={setDiscussing} />}
          </Card>

          <QuestionsCard projectId={p.id} />

          <FilesCard projectId={p.id} />

          {p.billing_model !== "maintenance" && p.billing_model !== "hosting" && (
            <Card title="Payment schedule">
              {p.milestones.length ? (
                <DataTable
                  label="Payment schedule"
                  rows={p.milestones}
                  rowKey={(m) => `${m.label}${m.when}`}
                  columns={[
                    { header: "Milestone", cell: (m) => m.label },
                    { header: "When", cell: (m) => m.when },
                    // Amounts: billing contacts only (UX-192).
                    ...(p.billing_visible
                      ? [
                          {
                            header: "Amount",
                            align: "right" as const,
                            cell: (m: Project["milestones"][number]) => (
                              <span className="mono">{formatMoney(m.amount)}</span>
                            ),
                          },
                        ]
                      : []),
                    { header: "Status", cell: (m) => <Status status={m.status} /> },
                  ]}
                />
              ) : (
                <EmptyState>Billed from logged hours.</EmptyState>
              )}
              <p className="faint" style={{ marginTop: 8 }}>
                {p.billing_visible ? "Amounts are before tax." : BILLING_ONLY_NOTE}
              </p>
            </Card>
          )}

          {first ? (
            <ActionBar
              info={<ActionInfo wide label="Deliverable awaiting your approval" value={first.title} />}
            >
              <DeliverableActions deliverable={first} />
            </ActionBar>
          ) : firstChange ? (
            <ActionBar
              info={
                <ActionInfo
                  wide
                  label={
                    firstChange.total
                      ? `Change request · ${formatMoney(firstChange.total)} incl. tax`
                      : "Change request"
                  }
                  value={`${firstChange.number}: ${firstChange.title}`}
                />
              }
            >
              <Link className="btn primary" to={`/quotes/${firstChange.id}`}>
                Review change
              </Link>
            </ActionBar>
          ) : null}
        </>
      )}
    </>
  );
}

/** Files the team shared on the project: images show a preview, and every file downloads. */
function FilesCard({ projectId }: { projectId: string }) {
  const files = useProjectFiles(projectId);
  const [viewing, setViewing] = useState<ProjectFile | null>(null);
  const url = (f: ProjectFile, suffix = "") => fileUrl(`/projects/${projectId}/files/${f.id}${suffix}`);
  if (files.isPending || files.error || !files.data.length) return null;
  return (
    <Card title="Files">
      <div className="item-list">
        {files.data.map((f) => (
          <div className="item" key={f.id}>
            <div className="file-name">
              <FileThumb
                filename={f.filename}
                src={f.has_preview ? url(f, "/preview") : null}
                onOpen={() => setViewing(f)}
              />
              <div className="what">
                <strong>{f.filename}</strong>
                <span>
                  {fileSize(f.size)} · shared {formatDate(f.shared_at)}
                </span>
              </div>
            </div>
            <a className="btn sm" href={url(f)} download={f.filename}>
              Download
            </a>
          </div>
        ))}
      </div>
      {viewing && (
        <Modal
          title={viewing.filename}
          wide
          onClose={() => setViewing(null)}
          footer={
            <a className="btn primary" href={url(viewing)} download={viewing.filename}>
              Download
            </a>
          }
        >
          <img className="file-full" src={url(viewing)} alt={viewing.filename} />
        </Modal>
      )}
    </Card>
  );
}

function DeliverableList({ deliverables, pending }: { deliverables?: Deliverable[]; pending: boolean }) {
  if (pending) return <Loading />;
  if (!deliverables?.length) return <EmptyState>No deliverables yet.</EmptyState>;
  return (
    <div className="item-list">
      {deliverables.map((d) => {
        const note = d.history.filter((h) => h.by_client && h.comment).at(-1)?.comment;
        return (
          <div className="item" key={d.id}>
            <div className="what">
              <Link to={`/deliverables/${d.id}`}>
                <strong>{d.title}</strong>
              </Link>
              <span>
                {d.phase_name} · <Status status={d.status} />
                {note && d.status === "changes_requested" ? ` · your note: “${note}”` : ""}
              </span>
              <DeliverableFiles deliverable={d} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Total Cost: invoiced plus unbilled time, e.g. "$10,100.00 (includes $3,330.00 in unbilled time)".
    Just the amount when nothing is unbilled. Billing contacts only: the API sends neither figure
    to anyone else, and then nothing shows. */
export function TotalCost({ budget: b }: { budget: Budget }) {
  if (!b.invoiced_amount || !b.unbilled_amount) return <span className="muted">-</span>;
  const unbilled = b.unbilled_amount.amount_minor;
  const total = { ...b.invoiced_amount, amount_minor: b.invoiced_amount.amount_minor + unbilled };
  return (
    <span className="mono">
      {formatMoney(total)}
      {unbilled > 0 && ` (includes ${formatMoney(b.unbilled_amount)} in unbilled time)`}
    </span>
  );
}

function OtherCurrencyNote({ budget: b }: { budget: Budget }) {
  if (!b.other_currency_invoices) return null;
  return (
    <p className="faint" style={{ marginTop: 10 }}>
      Some invoices are in another currency and aren&apos;t included.
    </p>
  );
}

/** Hours and money the way the project is billed (same as the Budget page's card). */
export function BudgetSummary({ project: p, budget: b }: { project: Project; budget?: Budget }) {
  const title = p.billing_model === "maintenance" ? "This month" : "Budget & hours";
  if (!b)
    return (
      <Card title={title}>
        <Loading />
      </Card>
    );
  if (b.maintenance) {
    const m = b.maintenance;
    return (
      <Card title={title}>
        <p className="faint" style={{ marginBottom: 8 }}>
          {formatDate(m.start_date)} – {formatDate(m.end_date)}
        </p>
        <Facts
          items={[
            ["Monthly balance", <span className="mono">{formatMoney(m.monthly_balance)}</span>],
            ["Rolled over", <span className="mono">{formatMoney(m.rolled_over)}</span>],
            ["Used", <span className="mono">{formatMoney(m.used)}</span>],
            ["Remaining", <span className="mono">{formatMoney(m.remaining)}</span>],
            ...(m.over.amount_minor > 0
              ? ([["Over balance", <span className="mono">{formatMoney(m.over)}</span>]] as [
                  string,
                  React.ReactNode,
                ][])
              : []),
            ...(b.invoiced_amount && b.unbilled_amount
              ? ([["Total Cost", <TotalCost budget={b} />]] as [string, React.ReactNode][])
              : []),
          ]}
        />
        <OtherCurrencyNote budget={b} />
        <p className="faint" style={{ marginTop: 10 }}>
          Unused balance rolls over for two more months, then expires (the oldest is used first).
        </p>
      </Card>
    );
  }
  // Not a billing contact (UX-192): hours, but no amounts or balances.
  if (b.billing_model === "maintenance" && p.billing_visible)
    return (
      <Card title={title}>
        <p className="muted">No maintenance month is open right now.</p>
        {b.invoiced_amount && b.unbilled_amount && (
          <Facts items={[["Total Cost", <TotalCost budget={b} />]]} />
        )}
        <OtherCurrencyNote budget={b} />
      </Card>
    );
  if (b.billing_model === "maintenance" && !p.billing_visible)
    return (
      <Card title={title}>
        <p className="muted">{BILLING_ONLY_NOTE}</p>
      </Card>
    );
  if (b.billing_model === "time_and_materials")
    return (
      <Card title={title}>
        <Facts
          items={[
            ["Billable hours", <span className="mono">{Number(b.billable_hours ?? 0)} h</span>],
            ...(p.billing_visible
              ? ([
                  ["Total Cost", <TotalCost budget={b} />],
                  ["Cap", b.cap ? <span className="mono">{formatMoney(b.cap)}</span> : "No cap"],
                ] as [string, React.ReactNode][])
              : []),
          ]}
        />
        <OtherCurrencyNote budget={b} />
      </Card>
    );
  if (b.billing_model === "fixed_fee") {
    const used = b.phases.reduce((s, x) => s + Number(x.hours_used), 0);
    const total = b.phases.reduce((s, x) => s + Number(x.budget_hours ?? 0), 0);
    return (
      <Card title={title}>
        <HoursBar used={used} budget={total || null} label="Hours used" />
        {p.budget_amount && (
          <Facts
            items={[["Fixed fee", <span className="mono">{formatMoney(p.budget_amount)} + tax</span>]]}
          />
        )}
      </Card>
    );
  }
  return (
    <Card title={title}>
      <EmptyState>Hosting is billed at a flat monthly rate.</EmptyState>
    </Card>
  );
}

function AgreementCard({ project: p }: { project: Project }) {
  const a = p.agreement!;
  return (
    <Card
      title={a.kind === "maintenance" ? "Maintenance agreement" : "Hosting agreement"}
      action={
        a.kind === "maintenance" && p.billing_visible ? (
          <a
            className="btn sm"
            href={fileUrl(`/projects/${p.id}/rate-card/pdf`)}
            target="_blank"
            rel="noopener"
          >
            Rate card
          </a>
        ) : undefined
      }
    >
      <Facts
        items={[
          ["Status", <Status status={a.status} />],
          ["Term", TERM[a.term] ?? a.term],
          ...(a.monthly_amount
            ? ([["Monthly", <span className="mono">{formatMoney(a.monthly_amount)} + tax</span>]] as [
                string,
                React.ReactNode,
              ][])
            : []),
          [
            "Billed",
            a.billing_frequency === "yearly"
              ? "Yearly"
              : a.billing_frequency === "quarterly"
                ? "Quarterly"
                : "Monthly",
          ],
          ["Started", formatDate(a.effective_date)],
          ["Ends", a.end_date ? formatDate(a.end_date) : "Renews automatically"],
        ]}
      />
    </Card>
  );
}
